// Sending an invoice through Stripe.
//
// Stripe raises the invoice, emails it from its own infrastructure and hosts the
// payment page. Chosen over emailing our own PDF because deliverability was the
// actual problem: invoices sent via Gmail SMTP were being quarantined silently
// by the recipient's provider, with no bounce and no way to know.
//
// The hosted page is also what makes on-site payment work: show the QR, the
// customer opens it on their own phone, and Apple Pay or Google Pay makes it two
// taps. No card reader, no native app.

/**
 * Raise and send a Stripe invoice for a job.
 * @returns {Promise<{hostedInvoiceUrl: string, number: string, invoicePdf: string, amountDue: number}>}
 */
export async function sendStripeInvoice({ jobId, invoiceId }) {
  if (!jobId) throw new Error('A job is required to send an invoice.');

  const call = firebase.functions().httpsCallable('createStripeInvoice');
  const { data } = await call({ jobId, invoiceId });

  if (!data || !data.success) {
    throw new Error((data && data.error) || 'Stripe declined to create the invoice.');
  }
  return data;
}

/**
 * A QR for the hosted payment page, as a data URI.
 * Sized and error-corrected for reading off a phone screen held at arm's length,
 * which is the actual use: the customer scans it from the operator's phone.
 */
export async function paymentQrDataUrl(hostedInvoiceUrl, { size = 320 } = {}) {
  if (!hostedInvoiceUrl) throw new Error('No hosted invoice URL to encode.');
  // Imported here rather than at module scope: a static import of vendor.js
  // pulls in bootstrap and therefore `document`, which makes this whole module
  // unimportable outside a browser - including in tests.
  const { loadQrCode } = await import('../vendor.js');
  const QRCode = await loadQrCode();
  return QRCode.toDataURL(hostedInvoiceUrl, {
    width: size,
    margin: 1,
    errorCorrectionLevel: 'M',
    color: { dark: '#0f172a', light: '#ffffff' },
  });
}

/** Human-readable amount from Stripe's integer cents. */
export function formatStripeAmount(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format((Number(cents) || 0) / 100);
}
